import type { ContentHash } from "@cogo/publisher-core";
import {
  createUiBlockConsumerReceiptV1,
  sameUiBlockClosureIdentity,
  uiBlockClosureIdentityOf,
  type UiBlockClosureIdentityV1,
  type UiBlockConsumerKind,
  type UiBlockConsumerObservationV1,
  type UiBlockConsumerReceiptV1,
} from "@cogo/schema/ui-block-e2e-closure";

export type UiBlockConsumerEvidenceSourceV1 =
  | "measured-native-consumer"
  | "measured-studio-core"
  | "measured-bot-headless"
  | "fixture"
  | "simulation";

export interface UiBlockConsumerArtifactHashesV1 {
  application: ContentHash;
  renderedOrLowered: ContentHash;
  actionFlowTrace: ContentHash;
  datasource: ContentHash;
}

export interface CreateUiBlockConsumerReceiptInputV1 {
  expectedIdentity: UiBlockClosureIdentityV1;
  observedIdentity: UiBlockClosureIdentityV1;
  consumer: UiBlockConsumerKind;
  expectedDesignReleaseHash: ContentHash;
  observedDesignReleaseHash: ContentHash;
  evidenceSource: UiBlockConsumerEvidenceSourceV1;
  observations: UiBlockConsumerObservationV1;
  artifactHashes: UiBlockConsumerArtifactHashesV1;
  evidenceRefs?: string[];
}

export function isPassingUiBlockActionFlowTraceV1(value: unknown): boolean {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const steps = (value as { steps?: unknown }).steps;
  return Array.isArray(steps) && steps.length > 0 && steps.every((step) => {
    if (!step || typeof step !== "object" || Array.isArray(step)) return false;
    return ["ok", "skipped"].includes(String((step as { status?: unknown }).status));
  });
}

export function createMeasuredUiBlockConsumerReceiptV1(
  input: CreateUiBlockConsumerReceiptInputV1,
): UiBlockConsumerReceiptV1 {
  const blockers: string[] = [];
  for (const [name, value] of Object.entries(input.artifactHashes)) {
    if (!/^sha256:[a-f0-9]{64}$/u.test(value)) {
      blockers.push(`consumer-artifact-hash-invalid:${name}`);
    }
  }
  const expected = uiBlockClosureIdentityOf(input.expectedIdentity);
  const observed = uiBlockClosureIdentityOf(input.observedIdentity);
  if (!sameUiBlockClosureIdentity(expected, observed)) {
    blockers.push("consumer-application-identity-mismatch");
  }
  if (!input.evidenceSource.startsWith("measured-")) {
    blockers.push(`consumer-evidence-source-not-measured:${input.evidenceSource}`);
  }
  if (input.expectedDesignReleaseHash !== expected.authorityHashes.design) {
    blockers.push("consumer-expected-design-release-mismatch");
  }
  if (input.observedDesignReleaseHash !== input.expectedDesignReleaseHash) {
    blockers.push("consumer-observed-design-release-mismatch");
  }
  if (input.artifactHashes.application !== observed.applicationHash) {
    blockers.push("consumer-application-artifact-hash-mismatch");
  }
  if (input.artifactHashes.datasource !== observed.documentHashes.datasource) {
    blockers.push("consumer-datasource-artifact-hash-mismatch");
  }
  for (const [name, value] of Object.entries(input.observations)) {
    if (value !== true) blockers.push(`consumer-observation-failed:${name}`);
  }

  const evidenceRefs = unique([
    ...(input.evidenceRefs ?? []),
    `ui-block:${input.consumer}:application:${input.artifactHashes.application}`,
    `ui-block:${input.consumer}:render:${input.artifactHashes.renderedOrLowered}`,
    `ui-block:${input.consumer}:flow:${input.artifactHashes.actionFlowTrace}`,
    `ui-block:${input.consumer}:datasource:${input.artifactHashes.datasource}`,
  ]);

  return createUiBlockConsumerReceiptV1({
    ...expected,
    consumer: input.consumer,
    designReleaseHash: input.observedDesignReleaseHash,
    status: blockers.length === 0 ? "pass" : "blocked",
    observations: structuredClone(input.observations),
    evidenceRefs,
    blockers: unique(blockers),
  });
}

function unique(values: string[]): string[] {
  return [...new Set(values.filter((value) => value.trim().length > 0))].sort(
    (left, right) => left.localeCompare(right),
  );
}
